import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row, Skeleton } from 'antd'

import getDiscountCodeInfo, { DiscountInfo } from 'core/application/account/discountCodeInfo'
import getLoginUser, { User } from 'core/application/account/loginUser'
import { setFirstLogin } from 'core/application/adminDashboard/adminTasks'
import MakePaymentValues from 'core/application/adminSignUp/makePayment'
import getSubscriptionFees, { SubscriptionFeesResponse } from 'core/application/subscriptions/getSubscriptionFees'

import TabPane from 'common/components/dataDisplay/TabPane'
import Tabs from 'common/components/dataDisplay/Tabs'
import Alert from 'common/components/feedback/Alert'
import Modal from 'common/components/feedback/Modal'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'
import StepItemPageHeader from 'common/components/onBoarding/StepItemPageHeader'

import useCurrentProduct from 'common/hooks/useCurrentProduct'
import useLoginUser from 'common/hooks/useLoginUser'

import { commandRequest } from 'common/RequestUtils'
import { convertRecurlyErrorsToMap, getCreditCardToken } from 'common/recultyUtils'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import ACHLogoActive from 'assets/images/ACHLogoActive'
import ACHLogoInactive from 'assets/images/ACHLogoInactive'
import CreditCardLogoActive from 'assets/images/CreditCardLogoActive'
import CreditCardLogoInactive from 'assets/images/CreditCardLogoInactive'
import gift from 'assets/images/gift.svg'

import { DiscountAlert } from '../../common/components'
import useQueryRequest from '../../common/hooks/useQueryRequest'
import { ROUTE_PATHS } from '../../common/routePaths'
import ACHPayment, { ACHFormValues } from './ACHPayment'
import CreditCardPayment from './CreditCardPayment'
import TodaysCharges from './TodaysCharges'
import WizardNextButton from './WizardNextButton'
import WizardPreviousButton from './WizardPreviousButton'

import './SubscriptionStyles.less'

interface MakePaymentProps extends BaseNavigationProps<MakePaymentValues> {
  creditCardPaymentHandler: (token: string, frequency: number) => Promise<any>
  achPaymentHandler: (token: string, billingEmail: string) => Promise<any>
  numberOfSteps: number
  currentStep: number
  titleCssClass?: string
}

const MakePayment: React.FC<MakePaymentProps> = (props) => {
  const history = useHistory()
  const { t: translate } = useTranslation()

  const [confirmPaymentModalVisibility, setConfirmPaymentModalVisibility] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [paymentType, setPaymentType] = useState<string>(Payments.ACH_KEY)
  const [creditCardRecurlyErrors, setCreditCardRecurlyErrors] = useState<Map<string, string>>(new Map())
  const [achReculryErrors, setAchReculryErrors] = useState<Map<string, string>>(new Map())
  const [achFormCompleted, setACHFormComplete] = useState<boolean>(false)
  const [achPaymentValues, setACHPaymentValues] = useState<ACHFormValues>(getEmptyACHValues())
  const [, updateUserInStore] = useLoginUser()

  const planCode = props.state.planCode ?? ''
  const frequency = props.state.frequency ?? 0
  const product = useCurrentProduct()
  const [fees, isLoadingFees, sentGetFeesRequest] = useQueryRequest<SubscriptionFeesResponse>(null)

  const [discount, isLoadingDiscount] = useQueryRequest<DiscountInfo>(null, () =>
    props.state.discountCode ? getDiscountCodeInfo(props.state.discountCode) : null,
  )

  const handleOk = async () => {
    const handler = paymentType === Payments.ACH_KEY ? handleAchPayment : handleCreditCardPayment
    handler()
      .then(() => {
        setFirstLogin('true')
        return updateUserInLocalStore()
      })
      .then(() => {
        redirectToDashboard()
      })
  }

  const handleCreditCardPayment = async () => {
    return new Promise<void>(async (resolve, reject) => {
      setLoading(true)
      try {
        const token = await getCreditCardToken()

        const result = await commandRequest(() => props.creditCardPaymentHandler(token, frequency))

        if (result.errors) {
          notification.open({
            message: translate('admin-signup.subscription.errors.went-wrong-message'),
            description: translate('admin-signup.subscription.errors.payment-error'),
            type: 'error',
          })
          setLoading(false)
          return
        }
        resolve()
      } catch (errors) {
        setCreditCardRecurlyErrors(errors)
        setLoading(false)
        setConfirmPaymentModalVisibility(false)
        reject()
      }
    })
  }

  const handleAchPayment = async () => {
    return new Promise<void>(async (resolve, reject) => {
      setLoading(true)

      try {
        const token = await getAchToken()

        const result = await commandRequest(() => props.achPaymentHandler(token, achPaymentValues.billingEmail))

        if (result.errors) {
          notification.open({
            message: translate('admin-signup.subscription.errors.went-wrong-message'),
            description: translate('admin-signup.subscription.errors.connection-error'),
            type: 'error',
          })
          setLoading(false)
          return
        }

        resolve()
      } catch (errors) {
        setAchReculryErrors(errors)
        setLoading(false)
        setConfirmPaymentModalVisibility(false)

        reject()
      }
    })
  }

  const getAchToken = () => {
    const billingInfo = {
      routing_number: achPaymentValues.bankRoutingNumber,
      account_number: achPaymentValues.accountNumber,
      account_number_confirmation: achPaymentValues.accountNumberConfirmation,
      account_type: achPaymentValues.bankAccountType,
      name_on_account: achPaymentValues.nameOnAccount,
      country: 'US',
    }

    return new Promise<any>((resolve, reject) => {
      recurly.bankAccount.token(billingInfo, async (err: any, token: any) => {
        if (err) {
          reject(convertRecurlyErrorsToMap(err))
        } else {
          resolve(token.id)
        }
      })
    })
  }

  const redirectToDashboard = () => {
    if (props.goNext) {
      props.goNext(props.name)
    }
    history.push(ROUTE_PATHS.DASHBOARD_PATH)
  }

  const updateUserInLocalStore = async () => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const user: User = await getLoginUser()
        updateUserInStore(user)
        resolve()
      } catch (errors) {
        notification.open(errors)
        reject()
      }
    })
  }

  const handleCancel = () => {
    setConfirmPaymentModalVisibility(false)
  }

  useEffect(() => {
    ;(async () => {
      if (planCode) {
        await sentGetFeesRequest(() => getSubscriptionFees(planCode, product.productId, props.state.seatNumber))
      }
    })()
  }, [planCode, props.state.seatNumber])

  const confirmPaymentModal = (
    <Modal
      visible={confirmPaymentModalVisibility}
      title={translate('admin-signup.subscription.payment.confirm-payment-modal-title')}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel}>
          {translate('general.cancel')}
        </Button>,
        <Button key="pay" type="primary" onClick={handleOk} loading={loading}>
          {translate('admin-signup.subscription.payment.pay-now-label')}
        </Button>,
      ]}
    >
      <div>
        <p>{translate('admin-signup.subscription.payment.confirm-payment-modal-body')}</p>
      </div>
    </Modal>
  )

  const handleChange = (key: string) => {
    setPaymentType(key)
  }

  return (
    <Skeleton loading={isLoadingFees || isLoadingDiscount}>
      {confirmPaymentModal}

      <div className={'t-admin-signup-make-payment'}>
        <Row>
          <Col xs={{ span: 22, offset: 1 }} lg={{ span: 16, offset: 3 }} xl={{ span: 8 }}>
            <StepItemPageHeader
              className="t-admin-signup-step-header"
              title={translate('admin-signup.subscription.title')}
              numberOfSteps={props.numberOfSteps}
              currentStep={props.currentStep}
              step={translate('admin-signup.subscription.payment.question')}
            />
          </Col>
        </Row>

        <Col span={24}>
          {fees && (
            <Row gutter={22}>
              <Col xs={{ span: 22, offset: 1 }} lg={{ span: 12, offset: 3 }} className="mb-32">
                {discount && (
                  <DiscountAlert
                    message={translate('admin-signup.subscription.payment.discount-applied-label')}
                    description={translate('admin-signup.subscription.payment.discount-applied-description')}
                  />
                )}
                <div className="mt-40">
                  <Tabs onChange={handleChange}>
                    <TabPane
                      key={Payments.ACH_KEY}
                      tab={
                        <p className="tab-content">
                          {paymentType === Payments.ACH_KEY ? <ACHLogoActive /> : <ACHLogoInactive />}
                          {translate('admin-signup.subscription.payment.ach-label')}
                        </p>
                      }
                    >
                      <ACHPayment
                        setACHFormComplete={setACHFormComplete}
                        setACHPaymentValues={setACHPaymentValues}
                        hasDirtyData={props.hasDirtyData}
                        ACHValues={getEmptyACHValues()}
                        recurlyErrors={achReculryErrors}
                      />
                    </TabPane>
                    <TabPane
                      tab={
                        <p className="tab-content">
                          {paymentType === Payments.CREDIT_CARD_KEY ? (
                            <CreditCardLogoActive />
                          ) : (
                            <CreditCardLogoInactive />
                          )}
                          {translate('admin-signup.subscription.payment.credit-card-label')}
                        </p>
                      }
                      key={Payments.CREDIT_CARD_KEY}
                    >
                      <CreditCardPayment recurlyErrors={creditCardRecurlyErrors} hasDirtyData={props.hasDirtyData} />
                    </TabPane>
                  </Tabs>
                </div>
              </Col>

              <Col xs={{ span: 22, offset: 1 }} lg={{ span: 8, offset: 0 }}>
                <TodaysCharges discount={discount} fees={fees} />
              </Col>
            </Row>
          )}
        </Col>

        <Row className="bottom__actions">
          <Col xs={{ span: 22, offset: 1 }} lg={{ span: 20, offset: 3 }}>
            <WizardPreviousButton
              onClick={() => {
                props.goBack(props.name)
              }}
            />

            {paymentType === Payments.ACH_KEY ? (
              <WizardNextButton
                text={translate('general.finish')}
                onClick={() => setConfirmPaymentModalVisibility(true)}
                disabled={!achFormCompleted}
              />
            ) : (
              <WizardNextButton
                text={translate('general.finish')}
                onClick={() => setConfirmPaymentModalVisibility(true)}
              />
            )}
          </Col>
        </Row>
      </div>
    </Skeleton>
  )
}

const getEmptyACHValues = (): ACHFormValues => {
  return {
    agreeTerms: false,
    bankAccountType: '',
    bankRoutingNumber: '',
    accountNumberConfirmation: '',
    accountNumber: '',
    billingEmail: '',
    nameOnAccount: '',
  }
}

const Payments = {
  ACH_KEY: 'ACH',
  CREDIT_CARD_KEY: 'credit-card',
}

export default MakePayment
